import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ui/accordion.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Button } from '@ui/button.tsx';
import { AccordionTriggerLabel } from '@admin/components/workspace/accordion-trigger-label.tsx';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { Input } from '@ui/input.tsx';
import { useRouter } from '@tanstack/react-router';
import { queryClient } from '@r/utils/utils.ts';
import { toast } from 'sonner';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { reloadResources } from 'i18next';
import { useTranslation } from 'react-i18next';
import { apiMediasPost, useCreateWorkspace, useInviteWorkspace } from '@r/api/generated/client.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import type {
  WorkspaceWorkspaceWriteModel,
  ConfigConfigReadModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export const CreateWorkspaceStep4 = () => {
  const { updateFormData } = useWorkspaceFormStore();
  const user = useUser();
  const activeWorkspace = user.data && getActiveWorkspaceUniversal(user.data.visibleWorkspaces);
  const activeWorkspaceId = activeWorkspace?.workspace.id as number;
  const router = useRouter();
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  // Fetch de toutes les configs disponibles (mutualisé avec les autres steps)
  const configsQuery = useAllConfigs();
  const configsFromApi = getConfigsFromResponse(configsQuery.data);

  const formSchema = z.object({
    regulation: z.instanceof(File, {
      message: t('form.workspace.legal.params.regulation.required')
    }),
    'privacy-policy': z.instanceof(File, {
      message: t('form.workspace.legal.params.privacy.policy.required')
    }),
    'legal-notice': z.instanceof(File, {
      message: t('form.workspace.legal.params.legal.notice.required')
    })
  });

  type FormSchema = z.infer<typeof formSchema>;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      regulation: undefined,
      'privacy-policy': undefined,
      'legal-notice': undefined
    },
    mode: 'onChange'
  });

  async function uploadFile(file: File, fileKey: keyof FormSchema) {
    const response = await apiMediasPost({ file, type: 'legal' });
    return { key: fileKey, ...response };
  }

  const inviteMutation = useInviteWorkspace({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        await queryClient.refetchQueries();
        await reloadResources();
        await router.invalidate();
        await router.navigate({ to: '/' });
      },
      onError: (error) => {
        // Si le backend exige une mise à jour du mot de passe, rediriger directement
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 && isOrvalErrorFromPath(error, 'post', '/admin/workspaces/invite')) {
          // Rafraîchir l'utilisateur pour mettre à jour `passwordNeedToBeChanged`, puis naviguer
          void user.refetch().finally(async () => {
            await router.navigate({ to: '/change-password' });
          });
          return;
        }
        showOrvalErrorToast(error, t('form.workspace.unknown.error'));
      }
    }
  });

  const submitMutation = useCreateWorkspace({
    mutation: {
      onSuccess: async () => {
        // Enchaîne avec l’invitation pour déclencher l’état passwordNeedToBeChanged côté backend
        const aw = activeWorkspace;
        const userId = user.data?.id;
        if (aw && userId) {
          const awAny = aw as unknown as {
            workspace?: { name?: string; domain?: string };
            uniqueCode?: string;
          };
          inviteMutation.mutate({
            data: {
              name: awAny.workspace?.name ?? '',
              domain: awAny.workspace?.domain ?? '',
              invitedUserWorkspace: {
                user: `/admin/users/${userId}`,
                uniqueCode: awAny.uniqueCode ?? ''
              }
            }
          });
        } else {
          await router.navigate({ to: '/' });
        }
      },
      onError: (error: unknown) => {
        if (isOrvalErrorFromPath(error, 'put', '/admin/workspaces')) {
          showOrvalErrorToast(error);
          return;
        }

        toast.error(t('form.workspace.unknown.error'));
      }
    }
  });

  async function onSubmit(values: FormSchema) {
    if (!configsFromApi?.length) {
      toast.error(t('form.workspace.configs.not.loaded'));
      return;
    }

    try {
      const uploadPromises = Object.entries(values).map(([fileType, file]) =>
        uploadFile(file as File, fileType as keyof FormSchema)
      );
      const uploadedFiles = await Promise.all(uploadPromises);

      // Trouver les configs media correspondantes
      const mediaKeys = ['regulation', 'privacy-policy', 'legal-notice'];
      const mediaConfigsMap = new Map<string, ConfigConfigReadModel>();

      for (const key of mediaKeys) {
        const config = findConfig(configsFromApi, 'media', key);
        if (config?.id) {
          mediaConfigsMap.set(key, config);
        }
      }

      // Vérifier que toutes les configs sont trouvées
      const missingConfigs = mediaKeys.filter((key) => !mediaConfigsMap.has(key));
      if (missingConfigs.length > 0) {
        toast.error(t('form.workspace.configs.not.found'));
        return;
      }

      // Construire les workspaceConfigs avec le nouveau format
      const workspaceConfigs = uploadedFiles.map((file) => {
        const config = mediaConfigsMap.get(file.key as string);
        if (!config?.id) {
          throw new Error(`Config not found for key: ${file.key}`);
        }

        return {
          value: file.iri as string,
          media: file.iri as string,
          config: `/configs/${config.id}`
        };
      });

      const newFormData = await updateFormData(activeWorkspaceId as number, {
        workspaceConfigs,
        status: 'active'
      });
      submitMutation.mutate({
        id: String(activeWorkspaceId),
        data: newFormData as unknown as WorkspaceWorkspaceWriteModel
      });
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'post', '/medias')) {
        showOrvalErrorToast(error);
        return;
      }

      toast.error(t('form.workspace.unknown.error'));
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0.5">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
          <AccordionItem key="item-1" value="item-1" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['regulation', 'privacy-policy', 'legal-notice']}
                label="Paramétres légaux"
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 space-y-4 bg-white rounded-b-lg">
              <FormField
                control={form.control}
                name="regulation"
                render={({ field: { value, onChange, ...fieldProps } }) => (
                  <FormItem>
                    <FormLabel>{t('form.workspace.legal.params.regulation')}</FormLabel>
                    <FormControl>
                      <Input
                        {...fieldProps}
                        placeholder={t('form.workspace.legal.params.regulation')}
                        type="file"
                        accept="application/pdf"
                        onChange={(event) => onChange(event.target.files?.[0])}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="privacy-policy"
                render={({ field: { value, onChange, ...fieldProps } }) => (
                  <FormItem>
                    <FormLabel>{t('form.workspace.legal.params.privacy.policy')}</FormLabel>
                    <FormControl>
                      <Input
                        {...fieldProps}
                        placeholder={t('form.workspace.legal.params.privacy.policy')}
                        type="file"
                        accept="application/pdf"
                        onChange={(event) => onChange(event.target.files?.[0])}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="legal-notice"
                render={({ field: { value, onChange, ...fieldProps } }) => (
                  <FormItem>
                    <FormLabel>{t('form.workspace.legal.params.legal.notice')}</FormLabel>
                    <FormControl>
                      <Input
                        {...fieldProps}
                        placeholder={t('form.workspace.legal.params.legal.notice')}
                        type="file"
                        accept="application/pdf"
                        onChange={(event) => onChange(event.target.files?.[0])}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Button
          rounded="full"
          loading={submitMutation.isPending || form.formState.isSubmitting}
          type="submit"
          disabled={!configsFromApi?.length || configsQuery.isPending}
        >
          {t('form.workspace.validate')}
        </Button>
      </form>
    </Form>
  );
};
